import React, { ChangeEvent, FormEvent, useCallback, useId, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Label } from '@ergeon/core-components';
import { getHelpSearchLocation } from '@ergeon/help-components';
import { MagnifyingGlassIcon } from '@ergeon/icons/hero/mini';

const FIELD_CLASSES = classNames(
  'flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5',
  'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500',
  'dark:border-gray-700 dark:bg-gray-900 dark:focus-within:border-blue-300 dark:focus-within:ring-blue-300',
);

const INPUT_CLASSES = classNames(
  'h-9 min-w-0 flex-1 bg-transparent paragraph-2 text-gray-900 outline-hidden',
  'placeholder:text-gray-600 dark:text-white dark:placeholder:text-gray-400',
);

type LocalSearchFormProps = {
  className?: string;
  nodeKey: string;
};

const LocalSearchForm = ({ className, nodeKey }: LocalSearchFormProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const fieldId = useId();
  const titleId = `${fieldId}-title`;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedQuery = query.trim();
    if (trimmedQuery) {
      navigate(getHelpSearchLocation(trimmedQuery, { localNodeKey: nodeKey }));
    }
  }, [navigate, nodeKey, query]);

  return (
    <form
      aria-labelledby={titleId}
      className={classNames('flex flex-col gap-2', className)}
      onSubmit={handleSubmit}
      role="search">
      <Label htmlFor={fieldId} id={titleId}>
        Search under this page
      </Label>
      <div className={FIELD_CLASSES}>
        <MagnifyingGlassIcon aria-hidden="true" className="size-4 shrink-0 text-gray-600 dark:text-gray-400" />
        <input
          className={INPUT_CLASSES}
          enterKeyHint="search"
          id={fieldId}
          name="q"
          onChange={handleChange}
          placeholder="Type and press Enter"
          type="search"
          value={query} />
      </div>
    </form>
  );
};

export default LocalSearchForm;
